import { Controller } from "@hotwired/stimulus"

// Keeps a per-todo "Part" <select> in sync with a single shared options
// template (id="custom_todo_part_options_template") rendered once on the
// figure edit page. After a part is created, renamed, or deleted the
// CustomPartsController emits a turbo_stream.replace targeting that
// template; the parent #custom_todo_part_options_carrier sees its
// children swapped, this controller's MutationObserver fires, and the
// select rebuilds its options.
//
// The currently-selected value is preserved when that part still
// exists. When the previously-selected part was deleted the value
// falls back to "" (— No part —) and a `change` event is dispatched
// so the inline-editor controller's dirty-state can react.
const TEMPLATE_ID = "custom_todo_part_options_template"

export default class extends Controller {
  connect() {
    const template = document.getElementById(TEMPLATE_ID)
    const parent = template?.parentElement
    if (!parent) return

    // Watching the parent (not the template itself) means we still get
    // notified after turbo_stream.replace removes the old template node
    // from the parent and inserts a fresh one with the same id.
    this._observer = new MutationObserver(() => this._sync())
    this._observer.observe(parent, { childList: true })
  }

  disconnect() {
    this._observer?.disconnect()
    this._observer = null
  }

  _sync() {
    const template = document.getElementById(TEMPLATE_ID)
    if (!template) return

    const previousValue = this.element.value
    this.element.innerHTML = template.innerHTML

    const stillExists = Array.from(this.element.options)
      .some((option) => option.value === previousValue)

    if (stillExists) {
      this.element.value = previousValue
    } else {
      this.element.value = ""
      // Notify the surrounding inline-editor (and anyone else listening)
      // that the field's value just changed, since setting `.value`
      // programmatically does not fire `change` on its own.
      this.element.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }
}
